import { useEffect, useRef, useState } from "react";
import { api, formatApiError, mediaUrl } from "../api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Upload, X, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

/**
 * Image picker. Provides a button that opens a media library dialog
 * to select an existing image or upload a new one. Returns the URL via onChange.
 */
export default function ImagePicker({ value, onChange, label = "Image", testId = "image-picker" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/media");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/media/upload", fd);
      setItems((arr) => [data, ...arr]);
      onChange(data.url);
      toast.success("Image uploaded");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const pick = (url) => {
    onChange(url);
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      {label && <div className="text-sm font-medium">{label}</div>}
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative w-24 h-24 rounded-md overflow-hidden border border-border bg-muted">
            <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80"
              data-testid={`${testId}-clear`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 rounded-md border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
            <ImagePlus className="w-5 h-5" />
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid={`${testId}-open`}
        >
          {value ? "Change image" : "Select image"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Media Library</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-3 pb-3 border-b">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-primary hover:bg-primary-dark text-white"
              data-testid={`${testId}-upload`}
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload new
            </Button>
            <div className="text-sm text-muted-foreground">
              {items.length} image{items.length === 1 ? "" : "s"}
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No images yet. Upload one to get started.
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3 max-h-[60vh] overflow-y-auto p-1">
              {items.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => pick(m.url)}
                  className="aspect-square rounded-md overflow-hidden border border-border hover:ring-2 hover:ring-primary transition-all bg-muted"
                  data-testid={`media-item-${m.id}`}
                >
                  <img src={mediaUrl(m.url)} alt={m.filename} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
